import React, { useContext, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControlLabel,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import Delete from '@mui/icons-material/Delete'
import Add from '@mui/icons-material/Add'
import Refresh from '@mui/icons-material/Refresh'
import API from '../../../../services/API.js'
import { GlobalContext, TenantSettingsContext } from '../../../../App.js'

const EditableQuestion = React.memo(function EditableQuestion({
  value,
  onCommit,
}) {
  const [localValue, setLocalValue] = useState(value)

  useEffect(() => {
    setLocalValue(value)
  }, [value])

  return (
    <TextField
      variant="outlined"
      size="small"
      fullWidth
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onBlur={() => onCommit(localValue)}
    />
  )
})

// Older settings may still store these as plain strings (before per-question
// polarity was added), or as objects mangled by that shape change - normalize
// anything we're given to a clean { question, flagOn } shape.
const normalizeQuestion = (q) => {
  if (typeof q === 'string') return { question: q, flagOn: 'Yes' }
  return {
    question: typeof q?.question === 'string' ? q.question : '',
    flagOn: q?.flagOn === 'No' ? 'No' : 'Yes',
  }
}

const defaultFitnessToPracticeQuestions = [
  {
    question: 'Are you fit to carry out your role without restriction?',
    flagOn: 'No',
  },
  {
    question:
      'Do you have any health conditions that may affect your ability to practice safely?',
    flagOn: 'Yes',
  },
  {
    question:
      'Are you currently taking any medication that could impair your performance at work?',
    flagOn: 'Yes',
  },
  {
    question:
      'Do you have any concerns about your fitness to practice you wish to raise?',
    flagOn: 'Yes',
  },
]

const defaultOccupationalHealthQuestions = [
  {
    question:
      'Have you had any significant illness or injury since your last appraisal?',
    flagOn: 'Yes',
  },
  {
    question:
      'Do you have any ongoing health concerns you would like support with?',
    flagOn: 'Yes',
  },
  {
    question:
      'Do you feel your current role is impacting your physical or mental wellbeing?',
    flagOn: 'Yes',
  },
  {
    question: 'Would you like to be referred to Occupational Health?',
    flagOn: 'Yes',
  },
]

const FlagOnSelect = ({ value, onChange, size = 'small' }) => (
  <FormControl size={size} sx={{ minWidth: 220 }}>
    <Select value={value ?? 'Yes'} onChange={(e) => onChange(e.target.value)}>
      <MenuItem value="Yes">Flag if answered "Yes"</MenuItem>
      <MenuItem value="No">Flag if answered "No"</MenuItem>
    </Select>
  </FormControl>
)

const QuestionList = ({ title, helperText, items, onChange, defaultQuestions }) => {
  const [newQuestion, setNewQuestion] = useState('')
  const [newFlagOn, setNewFlagOn] = useState('Yes')

  const normalizedItems = items.map(normalizeQuestion)

  const handleChangeAt = (index, changes) => {
    onChange(
      normalizedItems.map((item, idx) =>
        idx === index ? { ...item, ...changes } : item,
      ),
    )
  }

  const handleDeleteAt = (index) => {
    onChange(normalizedItems.filter((_, idx) => idx !== index))
  }

  const handleAdd = () => {
    if (!newQuestion.trim()) return
    onChange([
      ...normalizedItems,
      { question: newQuestion.trim(), flagOn: newFlagOn },
    ])
    setNewQuestion('')
    setNewFlagOn('Yes')
  }

  const hasBlankQuestion = normalizedItems.some((item) => !item.question)

  return (
    <Card elevation={0} variant="outlined" sx={{ borderRadius: 2 }}>
      <CardHeader
        title={title}
        subheader={helperText}
        titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
        action={
          defaultQuestions && (
            <Button
              size="small"
              onClick={() => onChange(defaultQuestions)}
              sx={{ mt: 1, mr: 1 }}
            >
              Reset to Defaults
            </Button>
          )
        }
      />
      <Divider />
      <CardContent>
        {hasBlankQuestion && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            One or more questions here are blank or corrupted (likely leftover
            from an earlier version of this settings page). Edit them below,
            delete them, or use "Reset to Defaults" to start fresh.
          </Alert>
        )}
        <Stack spacing={1}>
          {normalizedItems.map((item, i) => (
            <Stack
              key={i}
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={1}
            >
              <EditableQuestion
                value={item.question}
                onCommit={(newValue) =>
                  handleChangeAt(i, { question: newValue })
                }
              />
              <FlagOnSelect
                value={item.flagOn}
                onChange={(newValue) => handleChangeAt(i, { flagOn: newValue })}
              />
              <IconButton
                size="small"
                color="error"
                onClick={() => handleDeleteAt(i)}
              >
                <Delete />
              </IconButton>
            </Stack>
          ))}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={1}
          >
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Add a new question"
              value={newQuestion}
              onChange={(e) => setNewQuestion(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleAdd()
                }
              }}
            />
            <FlagOnSelect value={newFlagOn} onChange={setNewFlagOn} />
            <IconButton size="small" color="success" onClick={handleAdd}>
              <Add />
            </IconButton>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  )
}

const frequencyOptions = [
  { value: 6, label: 'Every 6 Months' },
  { value: 12, label: 'Annually' },
  { value: 24, label: 'Every 2 Years' },
]

const firstAppraisalOptions = [
  { value: 0, label: 'Immediately' },
  { value: 6, label: 'After 6 Months' },
  { value: 12, label: 'After 1 Year' },
  { value: 24, label: 'After 2 Years' },
]

const AppraisalSettings = () => {
  const [loading, setLoading] = useState(true)
  const [changesPresent, setChangesPresent] = useState(false)
  const [running, setRunning] = useState(false)
  const [runResult, setRunResult] = useState(null)
  const [enabled, setEnabled] = useState(false)
  const [schedulingMode, setSchedulingMode] = useState('automatic')
  const [frequencyMonths, setFrequencyMonths] = useState(12)
  const [firstAppraisalAfterMonths, setFirstAppraisalAfterMonths] = useState(12)
  const [reminderLeadWeeks, setReminderLeadWeeks] = useState(4)
  const [appraisalQuestions, setAppraisalQuestions] = useState([])
  const [preAppraisalQuestions, setPreAppraisalQuestions] = useState({
    fitnessToPractice: [],
    occupationalHealth: [],
  })

  const { setToast } = useContext(GlobalContext)
  const { setOrgConfig } = useContext(TenantSettingsContext)

  useEffect(() => {
    API.get('/settings?select=staffAppraisalSettings').then((res) => {
      if (res.status === 200) {
        const settings = res.data?.staffAppraisalSettings ?? {}
        setEnabled(settings.enabled !== false)
        setSchedulingMode(settings.schedulingMode ?? 'automatic')
        setFrequencyMonths(settings.frequencyMonths ?? 12)
        setFirstAppraisalAfterMonths(settings.firstAppraisalAfterMonths ?? 12)
        setReminderLeadWeeks(settings.reminderLeadWeeks ?? 4)
        setAppraisalQuestions(settings.appraisalQuestions ?? [])
        setPreAppraisalQuestions({
          fitnessToPractice: settings.preAppraisalQuestions?.fitnessToPractice ?? [],
          occupationalHealth: settings.preAppraisalQuestions?.occupationalHealth ?? [],
        })
      }
      setLoading(false)
    })
  }, [])

  const markChanged = () => setChangesPresent(true)

  const runSchedulerNow = () => {
    setRunning(true)
    setRunResult(null)
    API.post('/appraisals/run-scheduler-now')
      .then((res) => {
        if (res.status === 200) {
          setRunResult(res.data)
        }
      })
      .finally(() => setRunning(false))
  }

  const handlePromptChange = (key, newPrompt) => {
    setAppraisalQuestions((prev) =>
      prev.map((q) => (q.key === key ? { ...q, prompt: newPrompt } : q)),
    )
    markChanged()
  }

  const handleSaveChanges = () => {
    API.put('/site-config', {
      staffAppraisalSettings: {
        enabled,
        schedulingMode,
        frequencyMonths,
        firstAppraisalAfterMonths,
        reminderLeadWeeks,
        appraisalQuestions,
        preAppraisalQuestions,
      },
    }).then((res) => {
      if (res.status === 200) {
        setToast({ message: 'Changes Saved' })
        setChangesPresent(false)
        setOrgConfig((prev) => ({ ...prev, orgConfig: res.data }))
      }
    })
  }

  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={2}
      >
        <Stack>
          <Typography variant="title2" fontWeight={700}>
            Staff Appraisals
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure how often staff appraisals are carried out and
            customise the questions used.
          </Typography>
          {loading && <LinearProgress sx={{ mt: 2 }} />}
        </Stack>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={runSchedulerNow}
          disabled={running || !enabled}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {running ? 'Running...' : 'Run Scheduler Now'}
        </Button>
      </Stack>

      {runResult && (
        <Alert severity={runResult.skipped ? 'info' : 'success'}>
          {runResult.skipped
            ? (runResult.reason ?? 'Staff appraisals are disabled.')
            : `Checked ${runResult.employeesChecked} employee(s), created ${runResult.appraisalsCreated} new appraisal(s).`}
        </Alert>
      )}

      <Card elevation={0} variant="outlined" sx={{ borderRadius: 2 }}>
        <CardHeader
          title="Enable Staff Appraisals"
          subheader="When enabled, AmbuNet can automatically create appraisal records when staff are due and show appraisal prompts on dashboards."
          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
        />
        <Divider />
        <CardContent>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => {
                  setEnabled(e.target.checked)
                  markChanged()
                }}
              />
            }
            label={
              enabled
                ? 'Staff appraisals are enabled'
                : 'Staff appraisals are disabled'
            }
          />
          {!enabled && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Overnight appraisal checks and dashboard appraisal notifications
              will not run until this is enabled.
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card elevation={0} variant="outlined" sx={{ borderRadius: 2 }}>
        <CardHeader
          title="Appraisal Frequency"
          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
        />
        <Divider />
        <CardContent>
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Scheduling Mode</InputLabel>
              <Select
                value={schedulingMode}
                label="Scheduling Mode"
                onChange={(e) => {
                  setSchedulingMode(e.target.value)
                  markChanged()
                }}
              >
                <MenuItem value="automatic">
                  Automatic — based on start date and frequency
                </MenuItem>
                <MenuItem value="individual">
                  Individual — due dates set per employee
                </MenuItem>
              </Select>
              <FormHelperText>
                {schedulingMode === 'individual'
                  ? "Each employee's next appraisal date is set manually on their Appraisals tab. When an appraisal completes, the next date is set automatically from the frequency below and can be adjusted per employee. The First Appraisal setting is not used."
                  : "Appraisal dates are worked out automatically from each employee's start date and the frequencies below."}
              </FormHelperText>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Appraisal Frequency</InputLabel>
              <Select
                value={frequencyMonths}
                label="Appraisal Frequency"
                onChange={(e) => {
                  setFrequencyMonths(e.target.value)
                  markChanged()
                }}
              >
                {frequencyOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {schedulingMode === 'individual'
                  ? "How long after each completed appraisal should the employee's next appraisal date default to?"
                  : "How often should each employee's appraisal be repeated?"}
              </FormHelperText>
            </FormControl>

            <FormControl fullWidth disabled={schedulingMode === 'individual'}>
              <InputLabel>First Appraisal</InputLabel>
              <Select
                value={firstAppraisalAfterMonths}
                label="First Appraisal"
                onChange={(e) => {
                  setFirstAppraisalAfterMonths(e.target.value)
                  markChanged()
                }}
              >
                {firstAppraisalOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                How long after an employee starts should their very first
                appraisal be due? Choose "Immediately" to make it due as soon
                as they start. Every appraisal after that follows the standard
                frequency above.
              </FormHelperText>
            </FormControl>

            <TextField
              type="number"
              label="Reminder Lead Time (weeks)"
              value={reminderLeadWeeks}
              onChange={(e) => {
                setReminderLeadWeeks(Number(e.target.value))
                markChanged()
              }}
              helperText="How many weeks before an appraisal is due should it be created and the employee reminded?"
            />
          </Stack>
        </CardContent>
      </Card>

      <Card elevation={0} variant="outlined" sx={{ borderRadius: 2 }}>
        <CardHeader
          title="Appraisal Questions"
          subheader="These sections appear on every appraisal. You can edit the wording used for each."
          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
        />
        <Divider />
        <CardContent>
          <Stack spacing={1}>
            {appraisalQuestions.map((q) => (
              <EditableQuestion
                key={q.key}
                value={q.prompt}
                onCommit={(newValue) => handlePromptChange(q.key, newValue)}
              />
            ))}
          </Stack>
        </CardContent>
      </Card>

      <QuestionList
        title="Pre-Appraisal: Fitness to Practice Questions"
        helperText="Employees answer these before their appraisal is ready for HR review."
        items={preAppraisalQuestions.fitnessToPractice}
        defaultQuestions={defaultFitnessToPracticeQuestions}
        onChange={(items) => {
          setPreAppraisalQuestions((prev) => ({
            ...prev,
            fitnessToPractice: items,
          }))
          markChanged()
        }}
      />

      <QuestionList
        title="Pre-Appraisal: Occupational Health Questions"
        helperText="Employees answer these before their appraisal is ready for HR review."
        items={preAppraisalQuestions.occupationalHealth}
        defaultQuestions={defaultOccupationalHealthQuestions}
        onChange={(items) => {
          setPreAppraisalQuestions((prev) => ({
            ...prev,
            occupationalHealth: items,
          }))
          markChanged()
        }}
      />

      <Paper
        elevation={3}
        sx={{
          position: 'sticky',
          bottom: 0,
          p: 2,
          borderRadius: 3,
          background: '#ffffffcc',
          backdropFilter: 'blur(10px)',
          border: '1px solid #eee',
        }}
      >
        <Button
          size="large"
          variant="contained"
          fullWidth
          color="success"
          onClick={handleSaveChanges}
          disabled={!changesPresent}
        >
          Save Changes
        </Button>
      </Paper>
    </Stack>
  )
}

export default AppraisalSettings
